import type { Configuration } from '@azure/msal-browser';
import { LogLevel } from '@azure/msal-browser';
import { runtimeConfig } from '../config/runtime';

/**
 * MSAL configuration for Microsoft Entra ID authentication.
 *
 * Configuration values are resolved at runtime (via window.__RUNTIME_CONFIG__)
 * with fallback to Vite build-time env vars for local development.
 * See src/config/runtime.ts for the resolution order.
 */

const clientId = runtimeConfig.ENTRA_CLIENT_ID;
const tenantId = runtimeConfig.ENTRA_TENANT_ID;
const redirectUri = runtimeConfig.ENTRA_REDIRECT_URI;

export const msalConfig: Configuration = {
  auth: {
    clientId,
    authority: `https://login.microsoftonline.com/${tenantId}`,
    redirectUri,
    postLogoutRedirectUri: redirectUri,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      logLevel: import.meta.env.DEV ? LogLevel.Warning : LogLevel.Error,
      loggerCallback: (level: LogLevel, message: string, containsPii: boolean) => {
        if (containsPii) return;
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            break;
          case LogLevel.Warning:
            console.warn(message);
            break;
          default:
            break;
        }
      },
    },
  },
};

/**
 * Scopes to request when acquiring tokens for the StaffLens backend API.
 */
export const apiScopes = {
  scopes: [
    runtimeConfig.ENTRA_API_SCOPE || `api://${clientId}/access_as_user`,
  ],
};

/**
 * Scopes for the login request.
 * Includes the backend API scope so the user consents upfront and MSAL can
 * silently acquire API tokens after login.
 */
export const loginRequest = {
  scopes: [
    'openid',
    'profile',
    'email',
    ...apiScopes.scopes,
  ],
};
